/**
 * BeaconController - Player management component for Beacon mode.
 *
 * Uses coordinator hooks exclusively (zero legacy store imports).
 * Replaces the legacy Controller from LobbyV2/controller.tsx.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { peerCoordinator, appCoordinator, beaconCoordinator } from '../../coordinators/init';
import { useConnectedPlayers } from '../../coordinators/peer/hooks/useConnectedPlayers';
import { usePauseState } from '../../coordinators/peer/hooks/usePauseState';
import { useHostGamepads } from '../../coordinators/peer/hooks/useHostGamepads';
import { BeaconControllerPanel } from './components/BeaconControllerPanel';
import type { BeaconInputConfig } from '../../coordinators/beacon/types';
import { DEFAULT_BEACON_INPUT_CONFIG } from '../../coordinators/beacon/types';

export const BeaconController = () => {
  const players = useConnectedPlayers(peerCoordinator);
  const pauseState = usePauseState(peerCoordinator);
  const hostGamepads = useHostGamepads(appCoordinator);

  const [globalInputConfig, setGlobalInputConfig] = useState<BeaconInputConfig>(
    () => beaconCoordinator?.getInputConfig() ?? { ...DEFAULT_BEACON_INPUT_CONFIG },
  );

  useEffect(() => {
    if (!beaconCoordinator) return;
    const store = beaconCoordinator.getBeaconStore();
    const listenerId = store.addListener((state) => {
      setGlobalInputConfig(state.inputConfig);
    });
    return () => store.removeListener(listenerId);
  }, []);

  const hostGamepadIds = useMemo(
    () => hostGamepads.sort((a, b) => a.gamepadId - b.gamepadId).map((g) => g.gamepadId),
    [hostGamepads],
  );

  const [hasScrollbar, setHasScrollbar] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkForScrollbar = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setHasScrollbar(container.scrollHeight > container.clientHeight);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    checkForScrollbar();
    const observer = new ResizeObserver(checkForScrollbar);
    observer.observe(container);
    return () => observer.disconnect();
  }, [checkForScrollbar]);

  const scrollbarClasses = useMemo(
    () =>
      clsx(
        'h-full space-y-2 overflow-y-auto',
        hasScrollbar ? 'pr-2' : '',
        '[&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-slate-600',
        '[&::-webkit-scrollbar-track]:rounded-md [&::-webkit-scrollbar-track]:bg-slate-800',
        '[&::-webkit-scrollbar-track]:shadow-[inset_0_0_0_2px_rgb(var(--colors-primary))]',
        '[&::-webkit-scrollbar]:w-8 [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:bg-slate-800',
      ),
    [hasScrollbar],
  );

  return (
    <>
      <div className='flex gap-2'>
        <BeaconControllerPanel
          name='Host Controls'
          isGlobalPaused={pauseState.globalPaused}
          hostGamepadIds={hostGamepadIds}
          peerCoordinator={peerCoordinator}
          globalInputConfig={globalInputConfig}
          beaconCoordinator={beaconCoordinator!}
        />
      </div>
      <div className='border-primary bg-darker h-[317px] overflow-hidden rounded-lg border-2 md:h-[380px] lg:h-full lg:flex-1'>
        <div className='h-full p-2'>
          <div ref={scrollContainerRef} className={scrollbarClasses}>
            {players.map((player) => (
              <BeaconControllerPanel
                key={player.peerId}
                player={player}
                name={player.playerName}
                isGlobalPaused={pauseState.globalPaused}
                hostGamepadIds={hostGamepadIds}
                peerCoordinator={peerCoordinator}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
